/**
 * Static chrome + fallback payload for the acquisition "Running Campaign" detail.
 *
 * The wired real path (a Casino Ad Service campaign) OVERRIDES every figure here with
 * live data — hero Ad Spend, the KPI grid, Top 10 Websites and the generated banner
 * assets — keeping only the KPI titles/subtitles + section labels as the static chrome
 * (see `pages/campaign-detail`). The figure values below survive only as the fallback
 * for a legacy mock-pool campaign and as the base for the still-mock ended view
 * (`mock-data/ended-campaign`).
 */
import type { RunningCampaignData } from './types';

const ART = '/assets/marketing/campaign-halloween.png';

export const runningCampaignData: RunningCampaignData = {
  header: { title: 'Running Campaign Details' },
  hero: {
    image: ART,
    title: 'Halloween is coming!',
    subtitle: '100 Free Spins on our most popular Halloween games.',
    adSpend: { value: '$21,863', label: 'Ad Spend' },
    offering: { value: '100 Free Spins', label: 'Offering' },
    duration: { value: 'October 22 - October 30', label: 'Duration' },
  },
  performance: {
    kpis: [
      { id: 'cpa', title: 'CPA', subtitle: 'Cost per acquired player', today: { value: '$47', change: '+4.5%', changeType: 'increase' }, month: { value: '$71', change: '-5.5%', changeType: 'decrease' } },
      { id: 'registrations', title: 'Registrations', subtitle: 'New players joining your website', today: { value: '50', change: '+27.4%', changeType: 'increase' }, month: { value: '250', change: '+18%', changeType: 'increase' } },
      { id: 'ftds', title: 'FTDs', subtitle: 'Number of First Time Deposits', today: { value: '54', change: '+27.4%', changeType: 'increase' }, month: { value: '175', change: '+18%', changeType: 'increase' } },
      { id: 'ftd-value', title: 'FTD Value', subtitle: 'Average First Time Deposits Value', today: { value: '$54', change: '+8%', changeType: 'increase' }, month: { value: '$175', change: '+12%', changeType: 'increase' } },
      { id: 'turnover', title: 'Turnover', subtitle: 'Money wagered by players', today: { value: '50', change: '+27.4%', changeType: 'increase' }, month: { value: '250', change: '+18%', changeType: 'increase' } },
      { id: 'ad-spend', title: 'Ad Spend', subtitle: 'Budget spent for this campaign', today: { value: '$86', change: '+5%', changeType: 'increase' }, month: { value: '$1,200', change: '+2%', changeType: 'increase' } },
    ],
  },
  assets: {
    items: [
      { id: 'hero-v1', name: 'Hero Banner V1', type: 'Banner', width: 728, height: 90, channels: ['Web', 'Crypto'], tone: 'amber' },
      { id: 'hero-v2', name: 'Hero Banner V2', type: 'Banner', width: 728, height: 90, channels: ['Web', 'Crypto'], tone: 'ember' },
      { id: 'mobile-banner', name: 'Mobile Banner', type: 'Banner', width: 320, height: 50, channels: ['Apps', 'Crypto'], tone: 'tangerine' },
      { id: 'interstitial', name: 'Interstitial', type: 'Banner', width: 320, height: 480, channels: ['Apps', 'Crypto'], tone: 'violet' },
      { id: 'medium-rectangle', name: 'Medium Rectangle', type: 'Banner', width: 300, height: 250, channels: ['Web', 'Crypto'], tone: 'periwinkle' },
      { id: 'skyscraper', name: 'Skyscraper', type: 'Banner', width: 160, height: 600, channels: ['Web', 'Crypto'], tone: 'royal' },
    ],
    hasMore: true,
  },
  topWebsites: {
    items: [
      { id: 'coingecko', name: 'coingecko.com', url: 'https://www.coingecko.com', impressions: '48,200', clicks: '4,820' },
      { id: 'cointelegraph', name: 'cointelegraph.com', url: 'https://cointelegraph.com', impressions: '39,100', clicks: '3,910' },
      { id: 'reddit', name: 'reddit.com', url: 'https://www.reddit.com', impressions: '31,400', clicks: '3,140' },
      { id: 'youtube', name: 'youtube.com', url: 'https://www.youtube.com', impressions: '27,600', clicks: '2,760' },
      { id: 'decrypt', name: 'decrypt.co', url: 'https://decrypt.co', impressions: '21,800', clicks: '2,180' },
      { id: 'binance', name: 'binance.com', url: 'https://www.binance.com', impressions: '19,400', clicks: '1,940' },
      { id: 'tradingview', name: 'tradingview.com', url: 'https://www.tradingview.com', impressions: '16,100', clicks: '1,610' },
      { id: 'medium', name: 'medium.com', url: 'https://medium.com', impressions: '13,300', clicks: '1,330' },
      { id: 'telegram', name: 't.me', url: 'https://t.me', impressions: '11,200', clicks: '1,120' },
      { id: 'x', name: 'x.com', url: 'https://x.com', impressions: '9,800', clicks: '980' },
    ],
  },
};

export default runningCampaignData;
